import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useRequireAuth from "../hooks/useRequireAuth";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  ShoppingBasket,
  Trash2,
  ArrowUp,
  ArrowDown,
  X,
  GraduationCap,
  StickyNote,
  BookOpenCheck,
  Plus,
  MoveLeft,
  MoveRight,
  NotebookPen,
} from "lucide-react";
import { useBasket } from "../context/BasketContext";
import LessonBuilder from "./LessonBuilder";
import SaveToNotebookModal from "./SaveToNotebookModal";
import { formatHebrewRef, numberToHebrew } from "../lib/hebrewRef";
// (stripNikud no longer needed here — basket preserves nikud + ta'amim verbatim)
import { toast } from "sonner";

/**
 * BasketSheet — the user-visible "סל מקורות לשיעור" drawer.
 *
 * Always-open entry point:   <BasketFloatingButton />
 * Slide-in contents:         <BasketSheet />
 *
 * This component provides the complete basket workspace: add a personal note,
 * reorder, remove, clear, and hand off to the existing LessonBuilder for AI
 * generation. The LessonBuilder is reused with a new `mode="basket"` so we
 * don't duplicate the 3-step UX.
 */
export function BasketSheet({ open, onClose }) {
  const { t } = useTranslation();
  const { items, remove, clear, move, add, count } = useBasket();
  const [noteText, setNoteText] = useState("");
  const [showLB, setShowLB] = useState(false);
  const [showSaveNotebook, setShowSaveNotebook] = useState(false);
  const { gate, RequireAuthModal } = useRequireAuth();

  // Convert basket items into NotebookEntry payloads. Skip "note" items
  // since they belong to the basket workspace rather than the notebook.
  const notebookPayloads = useMemo(() => {
    return items
      .filter((it) => it.type !== "note")
      .map((it) => {
        if (it.type === "verse") {
          return {
            source_type: "verse",
            source_book: it.book,
            source_chapter: it.chapter,
            source_verse: it.verse,
            source_ref_he: formatHebrewRef(it.book_he || it.book, it.chapter, it.verse),
            content_he: it.he || "",
            content_text: it.en || "",
          };
        }
        if (it.type === "commentary") {
          return {
            source_type: "commentary",
            source_book: it.book,
            source_chapter: it.chapter,
            source_verse: it.verse,
            source_label: it.commentator_he || it.commentator,
            source_ref_he: formatHebrewRef(it.book_he || it.book, it.chapter, it.verse),
            content_he: it.he || "",
            content_text: it.en || "",
          };
        }
        if (it.type === "chapter") {
          const chHe = numberToHebrew(it.chapter);
          return {
            source_type: "verse",
            source_book: it.book,
            source_chapter: it.chapter,
            source_ref_he: `${it.book_he || it.book} פרק ${chHe}`,
            title: `פרק ${chHe}`,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [items]);

  const handleAddNote = () => {
    const text = noteText.trim();
    if (!text) return;
    add({ type: "note", text });
    setNoteText("");
    toast.success(t("basket_extra.added_toast"));
  };

  const handleClear = () => {
    if (items.length === 0) return;
    clear();
    toast.success(t("basket_extra.cleared_toast"));
  };

  const handleBuild = gate(
    () => {
      if (items.length === 0) {
        toast.error(t("basket_extra.empty_error"));
        return;
      }
      setShowLB(true);
    },
    { feature: "lesson" }
  );

  return (
    <>
      <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
        <SheetContent
          side="left"
          dir="rtl"
          className="w-full sm:max-w-lg md:max-w-xl overflow-y-auto bg-background border-l border-gold/30 p-0"
          data-testid="basket-sheet"
        >
          <div className="p-6 md:p-8 space-y-5">
            <SheetHeader className="text-right space-y-2">
              <SheetTitle className="font-heading text-2xl text-foreground flex items-center gap-2">
                <ShoppingBasket className="h-5 w-5 text-gold" strokeWidth={1.5} />
                {t("basket_extra.title_main")}
                <span
                  data-testid="basket-count"
                  className="font-latin text-sm text-muted-foreground"
                >
                  ({count})
                </span>
              </SheetTitle>
              <div className="ornamental-divider">✦</div>
            </SheetHeader>

            {items.length === 0 ? (
              <div className="text-center py-10 space-y-3">
                <ShoppingBasket
                  className="h-10 w-10 mx-auto text-muted-foreground/40"
                  strokeWidth={1.5}
                />
                <p className="font-heading text-sm text-muted-foreground">
                  {t("basket_extra.empty_short")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("basket_extra.empty_hint_action")}
                </p>
              </div>
            ) : (
              <ul
                className="space-y-3"
                data-testid="basket-items"
              >
                {items.map((it, idx) => (
                  <BasketItem
                    key={it.id}
                    item={it}
                    index={idx}
                    total={items.length}
                    onRemove={() => remove(it.id)}
                    onMoveUp={() => move(it.id, -1)}
                    onMoveDown={() => move(it.id, +1)}
                  />
                ))}
              </ul>
            )}

            <div className="gold-rule" />

            {/* Quick-add note */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <StickyNote className="h-4 w-4 text-gold" strokeWidth={1.5} />
                <h4 className="font-heading text-sm text-foreground">
                  {t("basket_extra.personal_note_header")}
                </h4>
              </div>
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder={t("basket_extra.note_placeholder")}
                dir="rtl"
                data-testid="basket-note-input"
                className="min-h-[70px] bg-card border-border/60 focus-visible:ring-gold font-body text-right"
              />
              <div className="mt-2 flex justify-end">
                <Button
                  onClick={handleAddNote}
                  disabled={!noteText.trim()}
                  data-testid="basket-add-note-btn"
                  size="sm"
                  className="sacred-btn"
                >
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  {t("basket_extra.add_to_basket_btn")}
                </Button>
              </div>
            </div>

            <div className="gold-rule" />

            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Button
                variant="ghost"
                onClick={handleClear}
                disabled={items.length === 0}
                data-testid="basket-clear-btn"
                className="sacred-btn-ghost text-muted-foreground"
                size="sm"
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                {t("basket_extra.clear_basket")}
              </Button>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  onClick={() => setShowSaveNotebook(true)}
                  disabled={notebookPayloads.length === 0}
                  data-testid="basket-save-notebook-btn"
                  variant="outline"
                  size="sm"
                  className="border-gold/40 text-foreground hover:bg-gold/10"
                >
                  <NotebookPen className="h-4 w-4" strokeWidth={1.5} />
                  {t("basket_extra.save_to_notebook")}
                </Button>
                <Button
                  onClick={handleBuild}
                  disabled={items.length === 0}
                  data-testid="basket-build-lesson-btn"
                  className="sacred-btn-gold"
                >
                  <GraduationCap className="h-4 w-4" strokeWidth={1.5} />
                  {t("basket_extra.build_lesson")}
                </Button>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Bulk save to notebook — sends every basket source as its own entry */}
      <SaveToNotebookModal
        open={showSaveNotebook}
        onClose={() => setShowSaveNotebook(false)}
        bulkPayloads={notebookPayloads}
        bulkLabel={t("basket_extra.n_sources_save_label", { count: notebookPayloads.length })}
      />

      {/* The existing Lesson Builder is reused in 'basket' mode. When the
          lesson is generated, items stay in the basket (user may want to
          tweak and regenerate). */}
      <LessonBuilder
        open={showLB}
        onClose={() => setShowLB(false)}
        mode="basket"
        basketItems={items}
      />
      {RequireAuthModal}
    </>
  );
}

function BasketItem({ item, index, total, onRemove, onMoveUp, onMoveDown }) {
  const { t } = useTranslation();
  // Always render references in their Hebrew form.
  // For "chapter" items (full-chapter selection) we omit the verse part.
  const verseRef =
    item.type === "verse" || item.type === "commentary"
      ? formatHebrewRef(item.book_he || item.book, item.chapter, item.verse)
      : "";
  const chapterRef =
    item.type === "chapter"
      ? formatHebrewRef(item.book_he || item.book, item.chapter, undefined, "chapter-only")
      : "";

  const label =
    item.type === "verse"
      ? verseRef
      : item.type === "commentary"
      ? `${item.commentator_he || item.commentator} — ${verseRef}`
      : item.type === "chapter"
      ? chapterRef
      : t("basket_extra.personal_note_label");

  const preview =
    item.type === "verse"
      ? item.he || item.en || ""
      : item.type === "commentary"
      ? item.he || item.en || ""
      : item.type === "chapter"
      ? t("basket_extra.verses_count", { count: item.verse_count || 0 })
      : item.text || "";

  const tag =
    item.type === "verse"
      ? t("basket_extra.tag_verse")
      : item.type === "commentary"
      ? t("basket_extra.tag_commentary")
      : item.type === "chapter"
      ? t("basket_extra.tag_chapter")
      : t("basket_extra.tag_note");

  const tagColor =
    item.type === "verse"
      ? "bg-gold/15 text-gold border-gold/40"
      : item.type === "commentary"
      ? "bg-blue-500/10 text-blue-500 border-blue-500/30"
      : item.type === "chapter"
      ? "bg-purple-500/10 text-purple-500 border-purple-500/30"
      : "bg-emerald-500/10 text-emerald-600 border-emerald-500/30";

  return (
    <li
      data-testid={`basket-item-${item.id}`}
      data-type={item.type}
      className="relative rounded-sm border border-border/60 bg-card/60 p-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`text-[10px] font-heading tracking-widest px-1.5 py-0.5 border rounded-sm uppercase ${tagColor}`}
            >
              {tag}
            </span>
            <span className="font-heading text-sm text-foreground truncate">
              {label}
            </span>
          </div>
          {preview && (
            <p
              dir="rtl"
              className="text-xs text-muted-foreground line-clamp-3 leading-relaxed"
            >
              {preview}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1 shrink-0">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            aria-label={t("basket_extra.move_up")}
            data-testid={`basket-item-up-${item.id}`}
            className="h-6 w-6 grid place-items-center rounded-sm text-muted-foreground hover:text-gold disabled:opacity-30 transition-colors"
          >
            <ArrowUp className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === total - 1}
            aria-label={t("basket_extra.move_down")}
            data-testid={`basket-item-down-${item.id}`}
            className="h-6 w-6 grid place-items-center rounded-sm text-muted-foreground hover:text-gold disabled:opacity-30 transition-colors"
          >
            <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            aria-label={t("basket_extra.remove")}
            data-testid={`basket-item-remove-${item.id}`}
            className="h-6 w-6 grid place-items-center rounded-sm text-muted-foreground hover:text-destructive transition-colors"
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </li>
  );
}

/**
 * Floating pill button for the basket — shown globally at the bottom of the
 * app whenever the basket has at least one item.
 */
export function BasketFloatingButton() {
  const { t } = useTranslation();
  const { count } = useBasket();
  const [open, setOpen] = useState(false);
  if (count === 0) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="basket-floating-button"
        aria-label={t("basket_extra.open_basket")}
        className="fixed bottom-20 left-5 z-50 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-card/95 px-4 py-2.5 text-sm font-heading text-foreground shadow-book backdrop-blur hover:border-gold hover:text-gold transition-all group"
      >
        <ShoppingBasket
          className="h-4 w-4 text-gold group-hover:scale-110 transition-transform"
          strokeWidth={1.5}
        />
        <span>{t("basket_extra.pill_label")}</span>
        <span
          data-testid="basket-floating-count"
          className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-gold text-background font-latin text-[11px]"
        >
          {count}
        </span>
      </button>
      <BasketSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
}
